import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectMarket } from '../../../../redux/app/configSlice';
import { getDevicesAsync, selectDevices } from '../../../../redux/devices/getDevicesSlice';
import { fetchRunSummaryDashboard } from '../../../../services/activeRunsService';
import { filterDevicesByMarket } from '../../../../services/flowApiService';
import {
  DEFAULT_RUN_SUMMARY,
  getSummaryErrorMessage,
  SUMMARY_FETCH_DEBOUNCE_MS,
  SUMMARY_LIMIT,
  toBackendDate,
} from '../utils/runSummaryUtils';
import {
  getBuildVersionsForFilters,
  resetRunSummaryFilters,
  selectBuildsFilters,
  selectDeviceTypeSelected,
  selectRunSummaryFilters,
  setDeviceTypeSelected,
  setRunSummaryFilters,
} from '../../../../redux/runSummary/runSummarySlice';
import { getDeviceIdForAppiumScripts } from '../../../../utilities/getDevideIdForAppiumScripts';

export default function useRunSummaryData() {
  const dispatch = useDispatch();
  const devices = useSelector(selectDevices);
  const market = useSelector(selectMarket);
  const filtersVersion = useSelector(selectBuildsFilters);
  const requestIdRef = useRef(0);
  const filters = useSelector(selectRunSummaryFilters);
  const [backendRunSummary, setBackendRunSummary] = useState(DEFAULT_RUN_SUMMARY);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(null);
  const deviceTypeSelected = useSelector(selectDeviceTypeSelected);

  // Device list and build versions are only needed to populate the filter
  // dropdowns, so fetch them lazily when those dropdowns are opened (and only once
  // — both are cached in Redux). This keeps them off the initial page load.
  const loadDevices = useCallback(() => {
    if (devices.length === 0) {
      dispatch(getDevicesAsync());
    }
  }, [devices.length, dispatch]);

  const loadBuildVersions = useCallback(() => {
    const hasBuilds =
      (filtersVersion.ios?.length ?? 0) > 0 || (filtersVersion.android?.length ?? 0) > 0;
    if (!hasBuilds) {
      dispatch(getBuildVersionsForFilters());
    }
  }, [filtersVersion, dispatch]);

  useEffect(() => {
    let isCurrent = true;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    const debounceId = setTimeout(async () => {
      setSummaryLoading(true);
      setSummaryError(null);

      try {
        const response = await fetchRunSummaryDashboard(
          SUMMARY_LIMIT,
          filters.deviceId || null,
          toBackendDate(filters.startDate),
          toBackendDate(filters.endDate),
          filters.androidBuildVersion || null,
          filters.iosBuildVersion || null,
        );

        if (!isCurrent || requestId !== requestIdRef.current) return;

        setBackendRunSummary(response?.runSummary || DEFAULT_RUN_SUMMARY);
      } catch (error) {
        if (!isCurrent || requestId !== requestIdRef.current) return;

        console.error('Failed to load run summary', error);
        setSummaryError(getSummaryErrorMessage(error));
        setBackendRunSummary(DEFAULT_RUN_SUMMARY);
      } finally {
        if (isCurrent && requestId === requestIdRef.current) {
          setSummaryLoading(false);
        }
      }
    }, SUMMARY_FETCH_DEBOUNCE_MS);

    return () => {
      isCurrent = false;
      clearTimeout(debounceId);
    };
  }, [filters]);

  const deviceOptions = useMemo(
    () =>
      filterDevicesByMarket(devices, market?.id)
        .filter(
          (device) =>
            !device.deleted_at &&
            !(device.type?.toLowerCase() === 'ios' && device.device_id_3 === null),
        )
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((device) => ({
          id: device.id,
          type: device.type,
          name: device.name,
          value: getDeviceIdForAppiumScripts(device),
        })),
    [devices, market],
  );

  const selectedDeviceLabel = useMemo(
    () =>
      filters.deviceId
        ? deviceOptions.find((device) => device.value === filters.deviceId)?.name ||
          filters.deviceId
        : 'All Devices',
    [deviceOptions, filters.deviceId],
  );

  const setFilterValue = useCallback((value) => dispatch(setRunSummaryFilters(value)), [dispatch]);
  const setDeviceTypeSelectedValue = useCallback(
    (value) => dispatch(setDeviceTypeSelected(value)),
    [dispatch],
  );

  const resetFilters = useCallback(() => dispatch(resetRunSummaryFilters()), [dispatch]);

  return {
    deviceOptions,
    filters,
    resetFilters,
    runSummary: backendRunSummary,
    selectedDeviceLabel,
    setFilterValue,
    summaryError,
    summaryLoading,
    filtersVersion,
    setDeviceTypeSelectedValue,
    deviceTypeSelected,
    loadDevices,
    loadBuildVersions,
  };
}
